import {
  Box,
  Button,
  Checkbox,
  MenuItem,
  TextField,
  Typography,
} from "@mui/material";
import {
  Control,
  Controller,
  FieldErrors,
  UseFormSetValue,
} from "react-hook-form";
import { ISKUForm, ISKUDeviceData, modelLabelMap } from "../variables/skuData";

interface SKUFormItemProps {
  index: number;
  device: ISKUDeviceData;
  deviceTypeOptions: string[];
  getModelsToRunForDeviceType: (type: string) => string[];
  onRemove: (index: number) => void;
  control: Control<ISKUForm>;
  errors: FieldErrors<ISKUForm>;
  watchedDeviceType: string;
  setValue: UseFormSetValue<ISKUForm>;
}

const SKUFormItem = ({
  index,
  device,
  deviceTypeOptions,
  getModelsToRunForDeviceType,
  onRemove,
  control,
  errors,
  watchedDeviceType,
  setValue,
}: SKUFormItemProps): JSX.Element => {
  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "flex-start",
        gap: 2,
        borderRadius: "8px",
        padding: "1em",
        flexWrap: "wrap",
      }}
    >
      <Typography sx={{ minWidth: "70px", mt: "16px" }}>
        Device {index + 1}
      </Typography>
      <Controller
        name={`skuData.${index}.deviceType`}
        control={control}
        defaultValue={device.deviceType}
        rules={{
          required: "Device Type is required",
        }}
        render={({ field }) => (
          <TextField
            select
            label="Device Type"
            value={field.value}
            sx={{ minWidth: 180 }}
            onChange={e => {
              field.onChange(e);

              // Pre-populate with ALL available models for this device type
              const selectedDeviceType = e.target.value;
              const allModelsForType =
                getModelsToRunForDeviceType(selectedDeviceType);

              setValue(`skuData.${index}.model`, allModelsForType);
            }}
            error={!!errors?.skuData?.[index]?.deviceType}
            helperText={
              errors?.skuData?.[index]?.deviceType
                ? "Device Type is required"
                : " "
            }
          >
            <MenuItem value="">
              <em>Select Device Type</em>
            </MenuItem>
            {deviceTypeOptions.map(type => (
              <MenuItem key={type} value={type}>
                {type}
              </MenuItem>
            ))}
          </TextField>
        )}
      />

      <Controller
        name={`skuData.${index}.model`}
        control={control}
        defaultValue={device.model || []}
        rules={{
          validate: value => {
            const validModels = (value || []).filter(
              (m: string) => m && m.trim() !== ""
            );

            return validModels.length > 0 || "At least one model is required";
          },
        }}
        render={({ field }) => (
          <TextField
            select
            label="Model(s)"
            value={field.value}
            onChange={event =>
              field.onChange(
                (event.target.value as unknown as string[]).filter(
                  val => val.trim() !== ""
                )
              )
            }
            SelectProps={{
              multiple: true,
              renderValue: selected =>
                (selected as string[])
                  .filter(model => model.trim() !== "")
                  .map(model => modelLabelMap[model] || model)
                  .join(", "),
            }}
            sx={{ minWidth: 220 }}
            error={!!errors?.skuData?.[index]?.model}
            helperText={errors?.skuData?.[index]?.model?.message || " "}
          >
            {getModelsToRunForDeviceType(watchedDeviceType).map(model => (
              <MenuItem key={model} value={model}>
                <Checkbox
                  checked={field.value?.includes(model)}
                  sx={{ marginRight: 1 }}
                />
                {modelLabelMap[model] || model}
              </MenuItem>
            ))}
          </TextField>
        )}
      />

      <Button
        variant="outlined"
        color="error"
        onClick={() => onRemove(index)}
        sx={{ mt: "16px" }}
      >
        Remove
      </Button>
    </Box>
  );
};

export default SKUFormItem;
